import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2, Mail, Building2, User, Briefcase, Phone, Globe2, MapPin } from 'lucide-react';
// Removed supabase; integrate with backend API instead
import { accessSignup } from '@/services/api/auth';
import { toast } from 'sonner';

interface AccessRequestFormProps {
  onSuccess?: () => void;
}

export const AccessRequestForm: React.FC<AccessRequestFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '',
    companyName: '',
    jobTitle: '',
    address: '',
    state: '',
    country: '',
    postCode: '',
    phoneNumber: '',
    businessNeed: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Major countries list for dropdown (extendable)
  const countries = useMemo(
    () => [
      'Australia',
      'Canada',
      'Denmark',
      'France',
      'Germany',
      'India',
      'Ireland',
      'Italy',
      'Japan',
      'Netherlands',
      'New Zealand',
      'Norway',
      'Singapore',
      'South Korea',
      'Spain',
      'Sweden',
      'Switzerland',
      'United Arab Emirates',
      'United Kingdom',
      'United States',
    ],
    []
  );

  // Simple business email validator: blocks personal and disposable providers.
  // For production-grade checks, also validate server-side (MX/domain allowlist).
  function isBusinessEmail(email: string): boolean {
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return false;
    const domainFull = value.split('@')[1] || '';
    const labels = domainFull.split('.').filter(Boolean);
    const domain = labels.length >= 2 ? `${labels[labels.length - 2]}.${labels[labels.length - 1]}` : domainFull;

    // Common personal/free providers (exact domains)
    const personalDomains = new Set([
      'gmail.com','googlemail.com','yahoo.com','ymail.com','rocketmail.com','hotmail.com','outlook.com','live.com','msn.com','icloud.com','me.com','mac.com','aol.com','proton.me','protonmail.com','gmx.com','gmx.de','zoho.com','yandex.com','yandex.ru','mail.com','pm.me'
    ]);
    // Very common typo-domains for those providers (catch "gamil.com" etc.)
    const personalTypos = new Set([
      // Gmail
      'gamil.com','gmial.com','gnail.com','gmaill.com','gmail.co','gmail.con','gmai.com',
      // Yahoo
      'yaho.com','yhoo.com','yahoo.co','yahoo.con',
      // Outlook/Hotmail/Live
      'outllook.com','outlook.co','hotmial.com','hotamil.com','liv.com',
      // iCloud/AOL/Proton
      'iclod.com','icloud.co','aol.co','aol.con','protonmaill.com','protomail.com',
    ]);
    // Common disposable/email testing providers (substring match)
    const disposableHints = ['mailinator','10minutemail','tempmail','guerrillamail','yopmail','trashmail','sharklasers','getnada','dropmail','dispostable'];

    if (personalDomains.has(domain) || personalTypos.has(domain)) return false;
    if (disposableHints.some((hint) => domainFull.includes(hint))) return false;
    // Block obvious example domains
    if (domainFull.endsWith('.example') || domainFull.includes('example.com')) return false;
    // Heuristic: require at least one dot and not just a TLD
    if (!domainFull.includes('.') || domainFull.split('.').some((p) => p.length === 0)) return false;
    return true;
  }

  // Field-level validator for businessEmail (used on blur)
  function getBusinessEmailError(value: string): string {
    const v = (value || '').trim();
    if (!v) return 'Email is required';
    const basic = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basic.test(v)) return 'Please enter a valid email address';
    if (!isBusinessEmail(v)) return 'Please use a business email (e.g., name@company.com). Personal providers are not accepted.';
    return '';
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) {
      newErrors.businessEmail = 'Please enter a valid email address';
    } else if (!isBusinessEmail(formData.businessEmail)) {
      newErrors.businessEmail = 'Please use a business email (e.g., name@company.com). Personal providers are not accepted.';
    }
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.postCode.trim()) newErrors.postCode = 'Post code is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';

    if (!formData.businessNeed.trim()) {
      newErrors.businessNeed = 'Business need description is required';
    } else if (formData.businessNeed.trim().length < 50) {
      newErrors.businessNeed = 'Please provide at least 50 characters explaining your business need';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await accessSignup({
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        businessEmail: formData.businessEmail,
        jobTitle: formData.jobTitle,
        address: formData.address,
        state: formData.state,
        country: formData.country,
        postCode: formData.postCode,
        phoneNumber: formData.phoneNumber,
        businessNeed: formData.businessNeed,
      });

  setSubmitted(true);
  // Defer success messaging to parent so it can close the modal and show a toast
  onSuccess?.();

    } catch (error: any) {
      console.error('Access request error:', error);
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to submit access request. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (submitted) {
    return (
      <Card className="shadow-card border-border/50">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Request Submitted Successfully!</h3>
              <p className="text-muted-foreground">
                Thank you for your interest in BillPortal. Our team will review your request 
                and contact you within 2-3 business days.
              </p>
            </div>
            <div className="bg-success/10 p-4 rounded-lg border border-success/20">
              <p className="text-success text-sm">
                We'll send updates to <strong>{formData.businessEmail}</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card border-border/50">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl font-semibold">Request Access to BillPortal</CardTitle>
        <p className="text-muted-foreground">
          Fill out this form and our team will review your request within 2-3 business days.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First & Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                First Name *
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-destructive' : ''}
                disabled={loading}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.firstName}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Last Name *
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-destructive' : ''}
                disabled={loading}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>

          {/* Business Email */}
          <div className="space-y-2">
            <Label htmlFor="businessEmail" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Business Email *
            </Label>
            <Input
              id="businessEmail"
              type="email"
              placeholder="john@company.com"
              value={formData.businessEmail}
              onChange={(e) => handleInputChange('businessEmail', e.target.value)}
              onBlur={(e) => {
                const msg = getBusinessEmailError(e.target.value);
                setErrors((prev) => ({ ...prev, businessEmail: msg }));
              }}
              className={errors.businessEmail ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.businessEmail && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.businessEmail}
              </p>
            )}
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="companyName" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Company Name *
            </Label>
            <Input
              id="companyName"
              type="text"
              placeholder="Acme Corporation"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              className={errors.companyName ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.companyName && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.companyName}
              </p>
            )}
          </div>

          {/* Job Title */}
          <div className="space-y-2">
            <Label htmlFor="jobTitle" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job Title *
            </Label>
            <Input
              id="jobTitle"
              type="text"
              placeholder="Finance Manager"
              value={formData.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              className={errors.jobTitle ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.jobTitle && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.jobTitle}
              </p>
            )}
          </div>

          {/* Address + State */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Address *
              </Label>
              <Input
                id="address"
                type="text"
                placeholder="123 Main Street"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={errors.address ? 'border-destructive' : ''}
                disabled={loading}
              />
              {errors.address && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.address}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="state" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                State *
              </Label>
              <Input
                id="state"
                type="text"
                placeholder="CA"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                className={errors.state ? 'border-destructive' : ''}
                disabled={loading}
              />
              {errors.state && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.state}
                </p>
              )}
            </div>
          </div>

          {/* Country + Post Code */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country" className="flex items-center gap-2">
                <Globe2 className="h-4 w-4" />
                Country *
              </Label>
              <select
                id="country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className={`h-10 w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${errors.country ? 'border-destructive' : 'border-input'}`}
                disabled={loading}
              >
                <option value="">Select a country</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.country && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.country}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="postCode" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Post Code *
              </Label>
              <Input
                id="postCode"
                type="text"
                placeholder="94016"
                value={formData.postCode}
                onChange={(e) => handleInputChange('postCode', e.target.value)}
                className={errors.postCode ? 'border-destructive' : ''}
                disabled={loading}
              />
              {errors.postCode && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.postCode}
                </p>
              )}
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone Number *
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              placeholder="+1 555 123 4567"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              className={errors.phoneNumber ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.phoneNumber && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.phoneNumber}
              </p>
            )}
          </div>

          {/* Business Need */}
          <div className="space-y-2">
            <Label htmlFor="businessNeed" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Business Need *
            </Label>
            <Textarea
              id="businessNeed"
              placeholder="Please describe how BillPortal would help your organization with bill processing, document management, or workflow automation. Include details about your current challenges and expected benefits."
              value={formData.businessNeed}
              onChange={(e) => handleInputChange('businessNeed', e.target.value)}
              className={`min-h-24 resize-none ${errors.businessNeed ? 'border-destructive' : ''}`}
              disabled={loading}
            />
            <div className="flex justify-between items-center">
              {errors.businessNeed ? (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.businessNeed}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Minimum 50 characters required
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.businessNeed.length}/500
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Access Request
          </Button>

          {/* Back to Login Button */}
          <Button 
            type="button"
            variant="outline" 
            className="w-full" 
            onClick={() => window.location.href = '/auth'}
          >
            Back to Login
          </Button>

          {/* Info Message */}
          <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
            <p className="text-primary text-sm">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              <strong>What happens next:</strong> Our team will review your request and contact you 
              within 2-3 business days. If approved, you'll receive an invitation to join BillPortal.
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
